import React, { useContext } from 'react';
import { LogoLoading } from 'ensightful-koala-ui';
import { styled } from '@mui/material';

import { useViewport } from 'contexts/viewport';
import { useAuth } from 'contexts/auth';
import { useUser2 } from 'contexts/user2';
import { PageContext } from 'pages/PeerEvaluation/PageProvider';
import { Button, Box, Dropdown, Icon, Text, TextInput, Stack, IconButton } from 'shell/components';
import ViewSubmissionsMobile from 'components/organisms/PeerEvaluation/ViewSubmissionsMobile';
import ViewSubmissionsDesktopTablet from 'components/organisms/PeerEvaluation/ViewSubmissionsTabletDesktop';

const ViewSubmissions = ({
    setIsFilterDrawerOpen,
    getSortedFilteredMembers,
    searchBarInput,
    onChangeSearchBarInput,
}) => {
    const { device } = useViewport();
    const { isLTI } = useAuth();
    const {
        currentPeerEvaluationId,
        currentPeerEvaluation,
        handleInstructorPages,
        handleOpenDialog,
        exportAsCSV,
        submissions,

        pushGradeLoading,
        pushGradesToLTI,

        // Constants
        DIALOG,
        INSTRUCTOR_PAGES,
    } = useContext(PageContext);
    const { isLoading, studentAccountUsers = [], groups = [] } = useUser2();

    const submisisonDropDownOptions = () => {
        if (isLTI) {
            return [
                { key: 'export-evaluations', value: 'Export Evaluations' },
                { key: 'grade-passback', value: 'Push Grades to LMS' },
                { key: 'send-reminder-to-all', value: 'Send Reminder to All' },
                { key: 'release-results', value: 'Release Results' },
                { key: 'delete', value: 'Delete' },
            ];
        }
        return [
            { key: 'export-evaluations', value: 'Export Evaluations' },
            { key: 'send-reminder-to-all', value: 'Send Reminder to All' },
            { key: 'release-results', value: 'Release Results' },
            { key: 'delete', value: 'Delete' },
        ];
    };

    const handleDropDown = (key, peerEvaluationId) => {
        switch (key) {
            case 'export-evaluations': {
                return exportAsCSV(peerEvaluationId);
            }
            case 'grade-passback': {
                if (pushGradeLoading) return;
                return pushGradesToLTI(currentPeerEvaluation, submissions);
            }
            case 'send-reminder-to-all': {
                const findMissingStudents = studentAccountUsers.filter(
                    (student) =>
                        !submissions.find((submission) => submission.owner_account_id === student.owner_account_id)
                );
                return handleOpenDialog(DIALOG.PEER_EVALUATION_NOTIFICATIONS_TO_ALL, {
                    id: currentPeerEvaluation.id,
                    accounts: findMissingStudents,
                });
            }
            case 'release-results': {
                if (!currentPeerEvaluation) return;
                return handleOpenDialog(DIALOG.PEER_EVALUATION_RELEASE_GRADES, {
                    ...currentPeerEvaluation,
                    release_to_students: true,
                    is_released: true,
                });
            }
            case 'delete': {
                return handleOpenDialog(DIALOG.PEER_EVALUATION_DELETE, { id: peerEvaluationId });
            }
            default:
                return;
        }
    };

    const renderHeaderMobile = () => {
        return (
            <Stack direction={'column'} spacing={6}>
                <Stack alignItems={'center'} direction={'row'} spacing={2} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <IconButton
                            aria-label={'back'}
                            kind={'arrowLeft'}
                            size={'medium'}
                            variant={'square'}
                            onClick={() => handleInstructorPages(INSTRUCTOR_PAGES.TABLE)}
                        />
                        <Text variant={'h6'} weight={'semibold'}>
                            {currentPeerEvaluation?.title}
                        </Text>
                    </Stack>
                    <Dropdown
                        id={`table-dropdown-${currentPeerEvaluationId}`}
                        options={submisisonDropDownOptions()}
                        rootComponent={
                            <IconButton
                                aria-label={'more options'}
                                data-cy={'three-dot-menu'}
                                kind={'moreHorizontal'}
                                size={'medium'}
                                variant={'square'}
                                onClick={(e) => e.stopPropagation()}
                            />
                        }
                        onSelect={({ key }) => handleDropDown(key, currentPeerEvaluationId)}
                    />
                </Stack>
                <CustomHeaderBox direction={'row'} spacing={2} alignItems={'center'}>
                    <Button
                        color={'neutral'}
                        startIcon={<Icon kind={'filter'} />}
                        variant={'tertiary'}
                        onClick={() => setIsFilterDrawerOpen(true)}
                    >
                        Filters
                    </Button>
                    <CustomMobileTextInput
                        disablePointerEventsOnIcon={!Boolean(searchBarInput)}
                        endIcon={
                            searchBarInput ? (
                                <IconButton
                                    aria-label={'clear search input'}
                                    kind={'exit'}
                                    variant={'square'}
                                    onClick={() => onChangeSearchBarInput('')}
                                    onMouseDown={(e) => e.preventDefault()}
                                />
                            ) : (
                                <Icon kind={'search'} />
                            )
                        }
                        placeholder={'Search'}
                        value={searchBarInput}
                        onChange={(e) => onChangeSearchBarInput(e.target.value)}
                    />
                </CustomHeaderBox>
            </Stack>
        );
    };

    const renderHeaderTablet = () => {
        return (
            <Stack direction={'column'} spacing={6}>
                <Stack direction={'row'} spacing={2} alignItems={'center'}>
                    <IconButton
                        aria-label={'back'}
                        kind={'arrowLeft'}
                        size={'medium'}
                        variant={'square'}
                        onClick={() => handleInstructorPages(INSTRUCTOR_PAGES.TABLE)}
                    />
                    <Text variant={'h6'} weight={'semibold'}>
                        {currentPeerEvaluation.title}
                    </Text>
                </Stack>
                <Stack alignItems={'center'} direction={'row'} spacing={2} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Button
                            color={'neutral'}
                            startIcon={<Icon kind={'filter'} />}
                            variant={'tertiary'}
                            onClick={() => setIsFilterDrawerOpen(true)}
                        >
                            Filters
                        </Button>
                        <CustomTextInput
                            disablePointerEventsOnIcon={!Boolean(searchBarInput)}
                            endIcon={
                                searchBarInput ? (
                                    <IconButton
                                        aria-label={'clear search input'}
                                        kind={'exit'}
                                        variant={'square'}
                                        onClick={() => onChangeSearchBarInput('')}
                                        onMouseDown={(e) => e.preventDefault()}
                                    />
                                ) : (
                                    <Icon kind={'search'} />
                                )
                            }
                            placeholder={'Search'}
                            value={searchBarInput}
                            onChange={(e) => onChangeSearchBarInput(e.target.value)}
                        />
                    </Stack>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Button
                            data-cy={'edit-evaluation-button'}
                            onClick={() => handleInstructorPages(INSTRUCTOR_PAGES.EDIT, currentPeerEvaluationId)}
                        >
                            Edit Evaluation
                        </Button>
                        <Dropdown
                            id={`table-dropdown-${currentPeerEvaluationId}`}
                            options={submisisonDropDownOptions()}
                            rootComponent={
                                <IconButton
                                    aria-label={'more options'}
                                    kind={'moreHorizontal'}
                                    size={'medium'}
                                    variant={'square'}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            }
                            onSelect={({ key }) => handleDropDown(key, currentPeerEvaluationId)}
                        />
                    </Stack>
                </Stack>
            </Stack>
        );
    };

    const renderHeaderDesktop = () => {
        return (
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
                <Stack direction={'row'} spacing={2} alignItems={'center'}>
                    <IconButton
                        aria-label={'back'}
                        kind={'arrowLeft'}
                        size={'medium'}
                        variant={'square'}
                        onClick={() => handleInstructorPages(INSTRUCTOR_PAGES.TABLE)}
                    />
                    <Text variant={'h6'} weight={'semibold'}>
                        {currentPeerEvaluation.title}
                    </Text>
                </Stack>
                <Stack direction={'row'} spacing={2} alignItems={'center'}>
                    <Button
                        color={'neutral'}
                        startIcon={<Icon kind={'filter'} />}
                        variant={'tertiary'}
                        onClick={() => setIsFilterDrawerOpen(true)}
                    >
                        Filters
                    </Button>
                    <CustomTextInput
                        disablePointerEventsOnIcon={!Boolean(searchBarInput)}
                        endIcon={
                            searchBarInput ? (
                                <IconButton
                                    aria-label={'clear search input'}
                                    kind={'exit'}
                                    variant={'square'}
                                    onClick={() => onChangeSearchBarInput('')}
                                    onMouseDown={(e) => e.preventDefault()}
                                />
                            ) : (
                                <Icon kind={'search'} />
                            )
                        }
                        placeholder={'Search'}
                        value={searchBarInput}
                        onChange={(e) => onChangeSearchBarInput(e.target.value)}
                    />
                    <Button
                        data-cy={'edit-evaluation-button'}
                        onClick={() => handleInstructorPages(INSTRUCTOR_PAGES.EDIT, currentPeerEvaluationId)}
                    >
                        Edit Evaluation
                    </Button>
                    <Dropdown
                        id={`table-dropdown-${currentPeerEvaluationId}`}
                        options={submisisonDropDownOptions()}
                        rootComponent={
                            <IconButton
                                aria-label={'more options'}
                                kind={'moreHorizontal'}
                                size={'medium'}
                                variant={'square'}
                                onClick={(e) => e.stopPropagation()}
                            />
                        }
                        onSelect={({ key }) => handleDropDown(key, currentPeerEvaluationId)}
                    />
                </Stack>
            </Stack>
        );
    };

    const responsiveHeader = {
        mobile: renderHeaderMobile(),
        tablet: renderHeaderTablet(),
        desktop: renderHeaderDesktop(),
    };

    const props = {
        currentPeerEvaluation: currentPeerEvaluation,
        students: getSortedFilteredMembers(),
        groups: groups,
        submissionList: submissions,
        handleOpenDialog: handleOpenDialog,
    };

    const responsiveSubmissionList = {
        mobile: <ViewSubmissionsMobile {...props} />,
        tablet: <ViewSubmissionsDesktopTablet {...props} />,
        desktop: <ViewSubmissionsDesktopTablet {...props} />,
    };

    if (isLoading) return <LogoLoading />;

    return (
        <Stack spacing={5}>
            {responsiveHeader[device]}
            <CustomStack>
                <Stack>{responsiveSubmissionList[device]}</Stack>
            </CustomStack>
        </Stack>
    );
};

const CustomStack = styled(Stack)(({ device, theme }) => {
    return {
        padding: theme.spacing(8, 7),
        ...(device === 'mobile' && {
            paddingBottom: theme.spacing(8),
        }),
        borderRadius: '12px 12px 0 0',
        width: '100%',
        minHeight: 'calc(100vh - 64px - 20px - 58px)',
        background: theme.palette.ensightful.shades[0],
    };
});

const CustomTextInput = styled(TextInput)(() => {
    return {
        width: '13.5rem',
    };
});

const CustomMobileTextInput = styled(TextInput)(() => {
    return {
        width: '100%',
    };
});

const CustomHeaderBox = styled(Box)(({ theme }) => {
    return {
        display: 'grid',
        gridTemplateColumns: '30% 70%',
        gridGap: theme.spacing(4),
        padding: theme.spacing(0, 4),
    };
});
export default ViewSubmissions;
